import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ActivityAttachments from '../../components/activity/ActivityAttachments';
import ActivityIntroScreen from '../../components/activity/ActivityIntroScreen';
import ActivityQuestionCard from '../../components/activity/ActivityQuestionCard';
import ActivitySubmittedBanner from '../../components/activity/ActivitySubmittedBanner';
import SubmitSuccessCelebration from '../../components/activity/SubmitSuccessCelebration';
import { OverallFeedbackSection } from '../../components/SubmissionFeedback';
import { BackNavLink } from '../../components/NavButtons';
import { getAnswerFile, isQuestionAnswered } from '../../utils/questionUtils';
import {
  getStudentExamById,
  startStudentExam,
  submitStudentExam,
} from '../../services/examService';
import { getClassTitle, getSubjectTitle } from '../../utils/classSubjectUtils';
import {
  canReviewExamAfterFinish,
  canShowExamResults,
  getExamSettings,
  getExamSubmissionScore,
  prepareExamQuestions,
} from '../../utils/examSettings';
import { focusTheme } from '../../utils/focusTheme';

function formatExamDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
  }).format(date);
}

function getSubmissionAnswers(exam) {
  return exam?.submission?.answers || exam?.submission?.examAnswers || exam?.answers || [];
}

function findExistingAnswer(question, exam) {
  const submissionAnswer = getSubmissionAnswers(exam).find(
    (answer) => answer.questionId === question.id || answer.question?.id === question.id
  );

  return question.answer || submissionAnswer || null;
}

function getAnswerValue(answer, key) {
  if (!answer) {
    return '';
  }

  if (key === 'selectedOptionId') {
    return (
      answer.selectedOptionId ||
      answer.optionId ||
      answer.selectedOption?.id ||
      answer.option?.id ||
      ''
    );
  }

  return answer.textAnswer || answer.answerText || answer.text || '';
}

function buildInitialAnswers(exam) {
  return (exam?.questions || []).reduce((acc, question) => {
    const answer = findExistingAnswer(question, exam);

    acc[question.id] = {
      selectedOptionId: getAnswerValue(answer, 'selectedOptionId'),
      textAnswer: getAnswerValue(answer, 'textAnswer'),
      file: getAnswerFile(answer),
    };

    return acc;
  }, {});
}

function hasSubmittedExam(exam) {
  return Boolean(
    exam?.submission?.submittedAt ||
      exam?.submission?.finishedAt ||
      exam?.submission?.status === 'submitted' ||
      exam?.submission?.status === 'finished' ||
      exam?.status === 'submitted'
  );
}

function getExamAvailability(exam) {
  const now = Date.now();
  const startAt = exam?.startAt ? new Date(exam.startAt).getTime() : null;
  const endAt = exam?.endAt ? new Date(exam.endAt).getTime() : null;

  if (hasSubmittedExam(exam)) {
    return 'submitted';
  }

  if (exam?.status === 'finished' || (endAt && endAt < now)) {
    return 'finished';
  }

  if (startAt && startAt > now) {
    return 'upcoming';
  }

  return 'active';
}

function getRandomizationNote(settings) {
  if (settings.randomizeQuestions && settings.randomizeOptions) {
    return 'ترتیب سوالات و گزینه‌ها به‌صورت تصادفی نمایش داده می‌شود.';
  }

  if (settings.randomizeQuestions) {
    return 'ترتیب سوالات به‌صورت تصادفی نمایش داده می‌شود.';
  }

  if (settings.randomizeOptions) {
    return 'ترتیب گزینه‌ها به‌صورت تصادفی نمایش داده می‌شود.';
  }

  return '';
}

function getSubmitCelebrationDescription({
  canReviewAfterFinish,
  canShowCorrectAnswers,
  canShowResults,
  score,
  maxScore,
}) {
  if (canShowResults && score != null) {
    return `آزمونت ثبت شد. نمره‌ات ${score}${maxScore != null ? ` از ${maxScore}` : ''} است.`;
  }

  if (canShowResults) {
    return 'آزمونت با موفقیت ثبت شد. نتیجه بعد از آماده‌شدن اینجا نمایش داده می‌شود.';
  }

  if (canReviewAfterFinish || canShowCorrectAnswers) {
    return 'آزمونت با موفقیت ثبت شد. می‌تونی پاسخ‌هات را مرور کنی.';
  }

  return 'آزمونت با موفقیت ثبت شد. مرور سوالات برای این آزمون فعال نیست.';
}

function ExamResultsBanner({ exam, score }) {
  const maxScore = exam?.maxScore;

  return (
    <div className="rounded-[10px] border border-emerald-200 bg-white px-4 py-3 shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
      <p className="text-[13px] font-bold text-emerald-700">نتیجه آزمون</p>
      {score != null ? (
        <p className="mt-1 text-[15px] font-bold text-slate-900">
          نمره شما: {score}
          {maxScore != null ? ` از ${maxScore}` : ''}
        </p>
      ) : (
        <p className="mt-1 text-[13px] leading-6 text-slate-600">
          نتیجه این آزمون هنوز آماده نشده است. بعد از تصحیح، نمره اینجا نمایش داده می‌شود.
        </p>
      )}
    </div>
  );
}

function ExamDetailsPage() {
  const { examId } = useParams();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasStartedSession, setHasStartedSession] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [showSuccessCelebration, setShowSuccessCelebration] = useState(false);

  const examSettings = getExamSettings(exam);
  const questions = useMemo(() => prepareExamQuestions(exam), [exam]);

  const answeredCount = useMemo(
    () => questions.filter((question) => isQuestionAnswered(question, answers[question.id])).length,
    [answers, questions]
  );

  const availability = getExamAvailability(exam);
  const hasSession = Boolean(exam?.submission);
  const hasStarted = hasSession || hasStartedSession || availability === 'submitted';
  const canStart = availability === 'active' && !hasSession && !hasStartedSession;
  const canAnswer = availability === 'active' && hasStarted && !hasSubmittedExam(exam);
  const canReviewAfterFinish = canReviewExamAfterFinish(exam);
  const canShowResults = canShowExamResults(exam) && availability === 'submitted';
  const canShowCorrectAnswers = Boolean(
    examSettings.showCorrectAnswers && availability === 'submitted'
  );
  const submissionScore = canShowResults ? getExamSubmissionScore(exam) : null;
  const showQuestionReview =
    availability !== 'submitted' || canReviewAfterFinish || canShowCorrectAnswers;
  const randomizationNote = getRandomizationNote(examSettings);

  useEffect(() => {
    let isMounted = true;

    async function loadExam() {
      setIsLoading(true);
      setErrorMessage('');
      setActionMessage('');

      try {
        const examData = await getStudentExamById(examId);

        if (isMounted) {
          setExam(examData);
          setAnswers(buildInitialAnswers(examData));
          setHasStartedSession(Boolean(examData?.submission));
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت آزمون وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadExam();

    return () => {
      isMounted = false;
    };
  }, [examId]);

  function updateAnswer(questionId, value) {
    if (!canAnswer) {
      return;
    }

    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: {
        selectedOptionId: currentAnswers[questionId]?.selectedOptionId || '',
        textAnswer: currentAnswers[questionId]?.textAnswer || '',
        file: currentAnswers[questionId]?.file || null,
        ...value,
      },
    }));
  }

  function buildAnswersPayload() {
    return questions.map((question) => {
      const answer = answers[question.id] || {};

      return {
        questionId: question.id,
        selectedOptionId: question.type === 'multiple_choice' ? answer.selectedOptionId || null : null,
        textAnswer: question.type === 'descriptive' ? (answer.textAnswer || '').trim() || null : null,
      };
    });
  }

  async function refreshExam() {
    const examData = await getStudentExamById(examId);
    setExam(examData);
    setAnswers(buildInitialAnswers(examData));
  }

  async function handleStartExam() {
    setIsStarting(true);
    setErrorMessage('');
    setActionMessage('');

    try {
      await startStudentExam(examId);
      setHasStartedSession(true);
      setActionMessage('جلسه آزمون شروع شد. حالا می‌تونی پاسخ‌ها رو وارد کنی.');
      await refreshExam();
    } catch (error) {
      setErrorMessage(error.message || 'شروع آزمون انجام نشد.');
    } finally {
      setIsStarting(false);
    }
  }

  async function handleSubmitExam() {
    const unansweredQuestions = questions.filter(
      (question) => !isQuestionAnswered(question, answers[question.id])
    );

    if (unansweredQuestions.length > 0) {
      setErrorMessage('برای ثبت نهایی، لطفا به همه سوالات آزمون پاسخ بده.');
      setActionMessage('');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setActionMessage('');

    try {
      await submitStudentExam(examId, buildAnswersPayload());
      await refreshExam();
      setActionMessage('آزمون با موفقیت ثبت شد.');
      setShowSuccessCelebration(true);
    } catch (error) {
      setErrorMessage(error.message || 'ثبت آزمون انجام نشد.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/exams">بازگشت به آزمون‌ها</BackNavLink>
        <div className="rounded-[14px] bg-white p-6 text-center text-[12px] text-slate-500 shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          در حال دریافت سوالات آزمون...
        </div>
      </div>
    );
  }

  if (errorMessage && !exam) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/exams">بازگشت به آزمون‌ها</BackNavLink>
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className={`text-[13px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className={`mt-3 rounded-[6px] px-5 py-1 text-[12px] font-bold ${focusTheme.button.retry}`}
          >
            تلاش دوباره
          </button>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/exams">بازگشت به آزمون‌ها</BackNavLink>
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className="text-[15px] font-bold text-slate-800">آزمون پیدا نشد</p>
          <p className="mt-2 text-[12px] text-slate-500">این آزمون در دسترس نیست.</p>
        </div>
      </div>
    );
  }

  if (availability === 'finished') {
    return (
      <div className="space-y-5">
        <BackNavLink to="/exams">بازگشت به آزمون‌ها</BackNavLink>

        <div className="flex items-center gap-2">
          <p
            style={{ background: focusTheme.header.background, boxShadow: focusTheme.header.boxShadow }}
            className="px-6 py-1 rounded-[10px] text-[15px] text-white"
          >
            آزمون پایان یافته
          </p>
          <img className="h-[50px]" src="/images/exam.webp" alt="" />
        </div>

        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className="text-[15px] font-bold text-slate-800">امکان ورود به این آزمون وجود ندارد</p>
          <p className="mt-2 text-[12px] leading-6 text-slate-500">
            مهلت شرکت در «{exam.title || 'این آزمون'}» به پایان رسیده است.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2 text-[10px]">
            <div className="rounded-[6px] bg-[#F8F8F8] px-3 py-2 text-slate-700">
              شروع: {formatExamDate(exam.startAt)}
            </div>
            <div className="rounded-[6px] bg-[#F8F8F8] px-3 py-2 text-slate-700">
              پایان: {formatExamDate(exam.endAt)}
            </div>
          </div>
          <Link
            to="/exams"
            className={`mt-4 inline-flex w-11/12 items-center justify-center rounded-[5px] py-1 text-[13px] font-bold ${focusTheme.button.primary}`}
          >
            بازگشت به لیست آزمون‌ها
          </Link>
        </div>
      </div>
    );
  }

  if (!hasStarted) {
    return (
      <div className="space-y-4">
        <ActivityIntroScreen
          attachments={exam.attachments}
          attachmentsTitle="فایل‌های پیوست آزمون"
          backLabel="بازگشت به آزمون‌ها"
          backTo="/exams"
          imageSrc="/images/exam.webp"
          isStarting={isStarting}
          onStart={canStart ? handleStartExam : undefined}
          startDisabled={!canStart}
          startLabel="شروع آزمون"
          startingLabel="در حال شروع آزمون..."
          stats={[
            {
              label: 'تعداد سوالات',
              value: `${questions.length} سوال`,
              background: focusTheme.stat.remaining,
            },
            {
              label: 'مدت آزمون',
              value: `${exam.durationMinutes || 0} دقیقه`,
              background: focusTheme.stat.completed,
            },
            {
              label: 'درس آزمون',
              value: [getSubjectTitle(exam), getClassTitle(exam, '')].filter(Boolean).join(' · ') || 'کلاس',
              background: focusTheme.stat.remaining,
            },
            {
              label: 'شروع',
              value: formatExamDate(exam.startAt),
              background: focusTheme.stat.completed,
            },
          ]}
          statusMessage={
            availability === 'upcoming' ? 'زمان شروع این آزمون هنوز نرسیده است.' : undefined
          }
          subtitle={
            availability === 'upcoming'
              ? 'بعد از رسیدن زمان شروع، می‌تونی جلسه آزمون را آغاز کنی.'
              : [
                  `زمان آزمون ${exam.durationMinutes || 0} دقیقه است. بعد از شروع، سوالات نمایش داده می‌شوند.`,
                  randomizationNote,
                ]
                  .filter(Boolean)
                  .join(' ')
          }
          title={exam.title || 'آزمون'}
        />

        {errorMessage && (
          <div className="rounded-[10px] bg-white p-3 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
            <p className={`text-[12px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SubmitSuccessCelebration
        open={showSuccessCelebration}
        onClose={() => setShowSuccessCelebration(false)}
        title="آفرین!"
        description={getSubmitCelebrationDescription({
          canReviewAfterFinish,
          canShowCorrectAnswers,
          canShowResults,
          score: submissionScore,
          maxScore: exam.maxScore,
        })}
      />
      <BackNavLink to="/exams">بازگشت به آزمون‌ها</BackNavLink>

      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <p
              style={{ background: focusTheme.header.background, boxShadow: focusTheme.header.boxShadow }}
              className="px-6 py-1 rounded-[10px] text-[15px] text-white truncate"
            >
              {exam.title || 'آزمون'}
            </p>
            <img className="h-[50px] shrink-0" src="/images/exam.webp" alt="" />
          </div>
        </div>

        <ActivityAttachments
          attachments={exam.attachments}
          title="فایل‌های پیوست آزمون"
        />

        {canAnswer && (
          <div
            className="rounded-[8px] px-3 py-2 text-slate-700"
            style={{ background: focusTheme.sectionBg }}
          >
            <p className="text-[13px] font-medium text-slate-800">پیشرفت پاسخ‌دهی</p>
            <p className="mt-1 text-[15px] font-bold text-slate-900">
              {answeredCount} از {questions.length} سوال
            </p>
          </div>
        )}

        {availability === 'submitted' && (
          <ActivitySubmittedBanner
            title="آزمون ثبت شده است"
            description={
              showQuestionReview
                ? 'پاسخ‌های شما با موفقیت ثبت نهایی شده‌اند.'
                : 'پاسخ‌های شما ثبت نهایی شده‌اند و مرور سوالات برای این آزمون مجاز نیست.'
            }
          />
        )}

        {canShowResults && <ExamResultsBanner exam={exam} score={submissionScore} />}

        {showQuestionReview && (
          <div
            className="flex flex-col gap-2 p-2 rounded-[8px]"
            style={{ background: focusTheme.sectionBg }}
          >
            {questions.length === 0 ? (
              <div className="rounded-[8px] bg-white px-4 py-6 text-center">
                <p className="text-[13px] font-bold text-slate-800">سوالی برای این آزمون ثبت نشده است</p>
              </div>
            ) : (
              questions.map((question, index) => (
                <ActivityQuestionCard
                  key={question.id}
                  answer={answers[question.id]}
                  canAnswer={canAnswer}
                  canShowCorrectAnswers={canShowCorrectAnswers}
                  entityId={examId}
                  index={index}
                  onChange={updateAnswer}
                  question={question}
                  showAnswerScore={canShowResults}
                  showQuestionFeedback={canShowResults}
                  slug="exams"
                />
              ))
            )}
          </div>
        )}

        {canShowResults && (
          <OverallFeedbackSection
            submission={exam.submission}
            accentColor={focusTheme.accent}
            showScore={false}
          />
        )}

        {(errorMessage || actionMessage) && (
          <div className="rounded-[10px] bg-white p-3 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
            {errorMessage && <p className={`text-[12px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>}
            {actionMessage && <p className={`text-[12px] font-bold ${focusTheme.message.success}`}>{actionMessage}</p>}
          </div>
        )}

        {availability === 'submitted' ? (
          <div className="w-full mx-auto flex flex-col gap-2 items-center justify-center">
            <p className="text-[13px] font-medium text-slate-700">
              {showQuestionReview
                ? 'پاسخ‌های این آزمون ثبت نهایی شده‌اند.'
                : 'مرور سوالات این آزمون بعد از ثبت مجاز نیست.'}
            </p>
          </div>
        ) : (
          <div className="w-full mx-auto flex flex-col gap-2 items-center justify-center">
            <p className="text-[13px] font-medium text-slate-800">
              بعد از پاسخ به همه سوالات، ثبت نهایی را بزن.
            </p>

            {canAnswer && (
              <button
                type="button"
                onClick={handleSubmitExam}
                disabled={isSubmitting || questions.length === 0}
                className={`w-11/12 mx-auto text-[15px] font-bold py-2.5 text-center flex items-center justify-center rounded-[8px] disabled:opacity-60 ${focusTheme.button.submit}`}
              >
                {isSubmitting ? 'در حال ثبت...' : 'ثبت نهایی آزمون'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExamDetailsPage;
